import React, { useEffect, useRef } from "react";
/* Every page's stylesheet, in the original order, before anything else
   (see the file). Pages share classes, so CSS is not split per page. */
import "./styles/pageStyles";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { LazyPage, pageFor, extrasFor } from "./pages/registry";
import KidoChat from "./components/KidoChat";
import CustomCursor from "./components/CustomCursor";
import { LocaleProvider } from "./i18n/LocaleContext";
import { LOCALES, stripLocale } from "./i18n/locale";
import { PUBLIC_ROUTES, LEGACY_REDIRECTS, localizePath } from "./seo/site.mjs";
import "./App.css";
/* Last CSS import in the app on purpose. Every page stylesheet is
   emitted ahead of it, so the contrast corrections it carries win
   on source order instead of needing heavier selectors. */
import "./styles/contrast.css";
import { trackPageView, installClickTracking } from "./lib/analytics";

/* Scroll to the top on a real page change — but NOT when the visitor
   only switched language (same page, different prefix), where holding
   the scroll position is what they expect. */
function ScrollToTop() {
  const { pathname } = useLocation();
  const lastBare = useRef(null);
  useEffect(() => {
    const bare = stripLocale(pathname);
    if (lastBare.current !== null && lastBare.current === bare) {
      lastBare.current = bare;
      return;
    }
    lastBare.current = bare;
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/* GA4 counts only the landing page by itself, so every later route
   change is reported here (see src/lib/analytics.js). The title is read
   a moment later, once SEO.jsx has set it for the new page. */
function PageViews() {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return undefined; }
    const id = window.setTimeout(() => trackPageView(pathname, document.title), 300);
    return () => window.clearTimeout(id);
  }, [pathname]);
  return null;
}

/* One document-level listener that reports clicks on links and buttons
   (WhatsApp, phone, email, outbound, buttons). Client only. */
function ClickTracking() {
  useEffect(() => { installClickTracking(); }, []);
  return null;
}

/* Every page is its own bundle, loaded on demand: src/pages/registry.js
   maps each public path (src/seo/site.mjs, the site map of record) to
   its module and throws at start-up if one is missing. Draft pages
   (DRAFT_ROUTES) are listed there too, behind the same flag. */

/* The Kido chat everywhere except ad landing pages, where the only
   action on offer is the registration form (owner's funnel brief,
   2026-10-05). Decided from the route, so the pre-rendered HTML and the
   browser agree. */
function ChatOutsideCampaigns() {
  const { pathname } = useLocation();
  const path = stripLocale(pathname).replace(/\/$/, "");
  if (path === "/give-and-learn" || path === "/autumn-term") return null;
  return <KidoChat />;
}

function RegisterFloatOnTrips() {
  const { pathname } = useLocation();
  const path = stripLocale(pathname).replace(/\/$/, "") || "/";
  if (!extrasFor(path).includes("registerFloat")) return null;
  return <LazyPage page="registerFloat" />;
}

export default function App() {
  return (
    <BrowserRouter>
      <LocaleProvider>
        <ScrollToTop />
        <PageViews />
        <ClickTracking />
        <Routes>
          {LOCALES.flatMap((locale) => [
            /* Every public page, once per language. */
            ...PUBLIC_ROUTES.map(({ path }) => {
              const { page, props } = pageFor(path);
              return (
                <Route
                  key={`${locale}:${path}`}
                  path={localizePath(path, locale)}
                  element={<LazyPage page={page} {...props} />}
                />
              );
            }),
            /* Legacy paths: Firebase Hosting already answers these with
               a 301 on a direct hit; this covers in-app navigation. */
            ...LEGACY_REDIRECTS.map(({ from, to }) => (
              <Route
                key={`${locale}:${from}`}
                path={localizePath(from, locale)}
                element={<Navigate to={localizePath(to, locale)} replace />}
              />
            )),
          ])}

          {/* Catch-all 404 — must be the LAST route. */}
          <Route path="*" element={<LazyPage page="notFound" />} />
        </Routes>
        {/* Persistent "Register Now" CTA, fixed bottom of the viewport
            across the Dubai trip routes (its bundle loads with that page). */}
        <RegisterFloatOnTrips />
        {/* Kido — site-wide chat widget. Stacks above the home-page
            WhatsApp float via CSS `body:has(.wa-float)`. */}
        <ChatOutsideCampaigns />
        {/* Sitewide FLC-green cursor. Off on coarse-pointer and
            reduced-motion devices; native OS cursor stays put there. */}
        <CustomCursor />
      </LocaleProvider>
    </BrowserRouter>
  );
}
